'use client';

import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useTransition } from 'react';

/**
 * Wraps filter/sort/pagination URL changes in `startTransition` so the currently
 * rendered collection grid stays visible while the next server render resolves.
 * Removes the need for a Suspense skeleton on filter changes and lets callers
 * show a subtle pending state via `isPending` if they want to.
 */
interface CollectionRouteNavigationValue {
	isPending: boolean;
	navigate: (href: string) => void;
}

const CollectionRouteNavigationContext = createContext<CollectionRouteNavigationValue | null>(null);

export function CollectionRouteNavigationProvider({ children }: { children: ReactNode }) {
	const router = useRouter();
	const [isPending, startTransition] = useTransition();

	const navigate = useCallback(
		(href: string) => {
			startTransition(() => {
				router.push(href);
			});
		},
		[router],
	);

	return (
		<CollectionRouteNavigationContext.Provider value={{ isPending, navigate }}>
			{children}
		</CollectionRouteNavigationContext.Provider>
	);
}

export function useCollectionRouteNavigation() {
	return useContext(CollectionRouteNavigationContext);
}
